import Vue from 'vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import isNull from 'lodash/isNull';
import isArray from 'lodash/isArray';
import _ from 'lodash';
import RuleEvaluator from '../../classes/RuleEvaluator';

Vue.component('hosted-form', {
    props: {
        formId: String,
        url: String,
        recaptchaKey: String,
        mode: String,
        formBuilder: {
            type: Array,
            required: false,
            default: () => Spark.state.formBuilder.items ?? [{description: '', fields: []}]
        },
        formBuilderData: {
            type: Object,
        },
        useCustomColorScheme: Boolean,
        customColorScheme: Object,
        baseColorScheme: Object,
        defaultTemplate: {
            type: Boolean,
            required: false,
            default: true
        },
        template: {
            type: String,
            required: false,
            default: 'default',
        },
        welcomeTitle: String,
        welcomeDescription: String,
        fileLimit: Number,
        openIdIdentifierField: String,
    },
    data() {
        return {
            error: null,
            validationErrors: {},
            isSuccessfullySent: false,
            isSending: false,
            form: {},
            newsletterData: [],
            showThankYou: false,
            keepLoading: false,
            currentStep: 0,
            colorScheme: this.customColorScheme,
            showWelcome: this.formBuilderData?.welcome_page_enabled ?? false,
            errorsTotal: 0,
            showErrorsHelper: false,
            isLoaded: false,
            openIdUser: null,
            formSubmitted: false, // Track if form has been submitted at least once
        }
    },
    mounted() {
        if (!isNull(this.recaptchaKey)) {
            Vue.use(VueReCaptcha, {siteKey: this.recaptchaKey});
        }

        this.setColorSetup();
        this.$nextTick(() => {
            this.isLoaded = true
        })

        if ((this.formBuilderData?.welcome_page_enabled ?? false) &&
            this.formBuilderData?.openid_verification_enabled
        ) {
            this.fetchUserInfo();
        }
    },
    created() {
        this.fetchFormState();
        if (!this.useCustomColorScheme) {
            this.colorScheme = this.baseColorScheme
        }

        // Check for error message in URL parameters (from redirects)
        const urlParams = new URLSearchParams(window.location.search);
        const errorCode = urlParams.get('code');
        const errorMessage = urlParams.get('message');

        if (errorCode && errorMessage) {
            // Show error message from URL
            this.setError(errorMessage);
            // Clean URL by removing error parameters
            const newUrl = window.location.pathname + window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
        }
    },
    computed: {
        formBuilderFieldsTypes() {
            let fields = {};
            _.map(this.formBuilder, step => {
                _.map(step.fields, item => {
                    fields[item.key] = item.type;
                });
            });

            return fields;
        },
        allFieldsFlat() {
            const out = [];
            (this.formBuilder || []).forEach(step => {
                (step.fields || []).forEach(f => out.push(f));
            });
            return out;
        },
        formRules() {
            const rules = this.formBuilderData?.rules;
            return _.isArray(rules) ? rules : [];
        },
        fieldVisibility() {
            return RuleEvaluator.computeVisibility(this.allFieldsFlat, this.formRules, this.form);
        },
    },
    watch: {
        fieldVisibility: {
            handler(map) {
                if (!map) return;
                this.allFieldsFlat.forEach(field => {
                    if (!field.key) return;
                    if (map.get(field.key) === false && this.form[field.key] !== undefined && this.form[field.key] !== null && this.form[field.key] !== '') {
                        if (_.isArray(this.form[field.key])) {
                            this.$set(this.form, field.key, []);
                        } else {
                            this.$set(this.form, field.key, '');
                        }
                    }
                });
            },
        },
    },
    methods: {
        isFieldVisible(item) {
            if (!item || !item.key) return true;
            const v = this.fieldVisibility.get(item.key);
            return v !== false;
        },
        closeWelcomePage() {
            if (this.formBuilderData && this.formBuilderData.openid_verification_enabled && !this.openIdUser?.sub) {
                this.loginWithOpenID();

                return null;
            }

            this.showWelcome = false
        },
        loginWithOpenID() {
            window.location.href = `/openid/login?form_id=${this.formId}`;
        },
        async openIdLogout(redirect = true) {
            try {
                const response = await this.$axios.post("/openid/logout");

                if (response.status === 200 && redirect) {
                    window.location.reload()
                }
            } catch (error) {
                //
            }
        },
        async fetchUserInfo() {
            try {
                const response = await this.$axios.get("/openid/user-info");
                this.openIdUser = response.data;

                if (this.openIdUser && this.openIdUser.email) {
                    this.showWelcome = false;
                }
            } catch (error) {
                //
            }
        },
        setColorSetup() {
            const rootStyles = document.querySelector(':root');
            rootStyles.style.setProperty('--button-background-color', this.colorScheme.buttonBackgroundColor);
            rootStyles.style.setProperty('--button-color', this.colorScheme.buttonColor);
            rootStyles.style.setProperty('--background-color', this.colorScheme.backgroundColor);
            rootStyles.style.setProperty('--text-color', this.colorScheme.textColor);
            rootStyles.style.setProperty('--link-color', this.colorScheme.linkColor);
        },
        handleEnterKey(event) {
            const target = event.target;

            if (target && target.tagName === 'BUTTON') {
                return;
            }

            if (target && target.tagName === 'TEXTAREA') {
                return;
            }

            event.preventDefault();
        },
        submit() {
            if (this.mode === 'preview') {
                return;
            }

            // Prevent duplicate submissions while request/captcha is in progress.
            if (this.isSending) {
                return;
            }
            // Mark that form submission was attempted
            this.formSubmitted = true;

            let hasErrors = false;
            //validate before sent form
            hasErrors = this.validateFormStep(this.currentStep);
            if (hasErrors) {
                // Scroll to first error
                const firstError = this.$refs.hostedForm?.querySelector('.has_error');
                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }

            // Start loading immediately to close the gap before sendForm() call.
            this.sendingStarted();

            if (isNull(this.recaptchaKey)) {
                this.sendForm();
                return;
            }

            this.getRecaptchaToken().then(recaptchaToken => {
                this.form.recaptcha = recaptchaToken;
                this.sendForm();
            }).catch(e => {
                console.error(e);
                this.sendingFinished();
            });
        },
        sendForm() {
            const data = new FormData();
            // Attach OpenID hidden fields
            if (
                this.openIdIdentifierField &&
                this.formBuilderData?.openid_verification_enabled &&
                this.openIdUser?.[this.openIdIdentifierField]
            ) {
                data.append(this.openIdIdentifierField, this.openIdUser?.[this.openIdIdentifierField])

                if (this.openIdUser?.uid) {
                    data.append('uid', this.openIdUser.uid);
                }

                if (this.openIdUser?.RB2B) {
                    data.append('RB2B', this.openIdUser.RB2B);
                }

                if (this.openIdUser?.secutix_customerNumber) {
                    data.append('customerNumber', this.openIdUser.secutix_customerNumber);
                }
            }

            Object.keys(this.form).forEach(key => {
                // Skip values from fields hidden by conditional logic.
                if (this.formBuilderFieldsTypes.hasOwnProperty(key) && this.fieldVisibility.get(key) === false) {
                    return;
                }
                if (this.formBuilderFieldsTypes.hasOwnProperty(key) && this.formBuilderFieldsTypes[key] === 'tel') {
                    data.append(key, this.form[key].formattedNumber || '');
                } else if (this.formBuilderFieldsTypes.hasOwnProperty(key) && this.formBuilderFieldsTypes[key] === 'date') {
                    // vuejs-datepicker (use-utc) gives a Date object; FormData would stringify it
                    // locale-dependently (e.g. "25. березень 2026"). Store a clean ISO YYYY-MM-DD instead.
                    const d = this.form[key];
                    data.append(key, d instanceof Date ? d.toISOString().slice(0, 10) : '');
                } else {
                    if (typeof this.form[key] === 'object' && !!this.form[key][Object.keys(this.form[key])[0]]?.name) {
                        for (let i = 0; i < this.form[key].length; i++) {
                            let file = this.form[key][i];
                            data.append(`${key}[${i}]`, file);
                        }
                        return;
                    }
                    // Handle arrays (e.g., booking_widget selections)
                    if (isArray(this.form[key])) {
                        this.form[key].forEach((value, index) => {
                            data.append(`${key}[${index}]`, value);
                        });
                    } else {
                        data.append(key, this.form[key]);
                    }
                }
            });

            //add some service data
            this.newsletterData.forEach((key) => {
                data.append('_service_bl_newsletter[]', key);
            });

            this.$axios.post(this.url, data, {
                withCredentials: false, headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
                .then(this.handleResponse)
                .catch(this.handleError)
                .finally(this.sendingFinished);
        },
        successfullySent(data) {
            if (this.openIdUser && this.openIdUser.email) {
                this.openIdLogout(false);
            }

            if (data.redirect && this.defaultTemplate) {
                this.keepLoading = true;
                return window.location.href = data.redirect;
            }
            this.showThankYou = true;
        },
        handleError(error) {
            // Handle request errors
            this.setError(error.message || 'Something went Wrong');
        },
        handleResponse({data}) {
            switch (data.status) {
                case 200:
                    // Handle successful submit
                    this.successfullySent(data);
                    break;
                case 204:
                    // Handle form not found or wrong token
                    // Prefer data.error over data.message for 204 status
                    this.setError(data.error || data.message || 'Form Not Found or Wrong Form Token');
                    break;
                case 422:
                    // Handle validation errors
                    this.setValidationErrors(data.data);
                    break
                default:
                    if (data.redirect) {
                        this.keepLoading = true;
                        window.location.href = data.redirect;
                        break;
                    }
                    this.setError(data.message || data.error || 'An error occurred');
                    break;
            }
        },
        sendingStarted() {
            this.isSending = true;
            this.$root.isLoading = true;
            this.clearErrors();
        },
        sendingFinished() {
            this.resetFormState();

            if (!this.keepLoading) {
                this.isSending = false;
                this.$root.isLoading = false;
            }
        },
        clearErrors() {
            this.error = null;
            this.validationErrors = {};
        },
        setError(message) {
            this.error = message;
        },
        setValidationErrors(errors) {
            this.validationErrors = errors;

            // Navigate to the step that contains the first field with an error
            try {
                const stepIndex = this.getFirstErrorStepIndex(errors);
                if (stepIndex !== null && stepIndex !== undefined && stepIndex !== this.currentStep) {
                    this.currentStep = stepIndex;
                    this.saveFormState();

                    this.$nextTick(() => this.checkStepErrors(true));
                }
            } catch (e) {
                // noop
            }
        },
        getFirstErrorStepIndex(errors) {
            if (!errors) {
                return null;
            }

            const errorKeys = Object.keys(errors);
            // Prefer field-specific keys (like "field" or "field.0")
            for (let i = 0; i < errorKeys.length; i++) {
                const rawKey = errorKeys[i];
                if (!rawKey || rawKey === 'files_max_size') {
                    continue; // skip global keys
                }

                const fieldKey = rawKey.split('.')[0];
                // find step containing fieldKey
                for (let s = 0; s < this.formBuilder.length; s++) {
                    const step = this.formBuilder[s];
                    if (!step || !Array.isArray(step.fields)) continue;
                    const found = step.fields.find(f => f && f.key === fieldKey);
                    if (found) {
                        return s;
                    }
                }
            }

            return null;
        },
        resetForm() {
            this.clearErrors();
            Object.keys(this.form).forEach(key => {
                if (!key.startsWith('_')) {
                    this.form[key] = null;
                }
            });
            this.showThankYou = false;
            this.newsletterData = [];
        },
        pushFile(files, fieldKey) {
            this.form[fieldKey] = files;
            this.validateFormStep(this.currentStep, fieldKey);
        },

        /**
         * Get Google Re-Captcha token
         *
         * @returns {Promise<string>}
         */
        getRecaptchaToken() {
            return this.$recaptchaLoaded().then(() => {
                return this.$recaptcha('login');
            })
        },
        pushCheckboxGroup(e, fieldKey) {
            if (!isArray(this.form[fieldKey])) {
                this.form[fieldKey] = [e.target.value];
            }
        },
        pushNewsletterCheckbox($event, fieldKey) {
            this.pushCheckboxGroup($event, fieldKey);

            //newsletterData
            const key = $event.target.name.trim();
            const index = this.newsletterData.indexOf(key);
            if (index > -1) {
                this.newsletterData.splice(index, 1);
            } else {
                this.newsletterData.push(key);
            }
        },
        setStep(step) {
            let hasErrors = false;
            //validate on next step action
            if (step > this.currentStep) {
                hasErrors = this.validateFormStep(this.currentStep);
            }

            if (step < 0) {
                this.currentStep = 0;
                this.saveFormState();
                return
            }

            if (!hasErrors) {
                this.currentStep = step;
                this.saveFormState();
                this.checkStepErrors(true);
            }
        },
        validateFormStep(step = this.currentStep, el = null) {
            let hasErrors = false;
            let form = this.$refs.hostedForm;
            let formGlobal = this.form;
            const RBOpenIdRegex = {
                'given_name': '^[^0-9?´`^!§%&$()=;",:#<>|°*+\\{}\\/€_@~µ\\[\\[²³]{1,39}?$',
                'family_name': '^[^0-9?´`^!§%&$()=;",:#<>|°*+\\{}\\/€_@~µ\\[\\[²³]{1,39}?$'
            };

            // Fallback for Firefox Android where checkValidity()/validity.valueMissing may not work (GeckoView bug #1510450)
            const isRequiredAndEmpty = (input, formEl) => {
                if (!input || !input.required) return false;
                if (input.type === 'radio') {
                    const group = formEl?.querySelectorAll(`input[name="${input.name}"]`);
                    return !group?.length || ![...group].some(r => r.checked);
                }
                if (input.type === 'checkbox') {
                    return !input.checked;
                }
                if (input.type === 'file') {
                    return !input.files?.length;
                }

                const val = (input.value || '').trim();

                return val === '';
            };

            const normalizeUrlValue = (item) => {
                if (item.type !== 'url') return;

                const value = item.value?.trim();
                if (!value) return;

                if (/^(https?:)?\/\//i.test(value)) return;


                if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value)) {
                    const normalized = 'https://' + value;

                    item.value = normalized;

                    if (this.form && this.form.hasOwnProperty(item.id)) {
                        this.$set(this.form, item.id, normalized)
                    }

                    item.dispatchEvent(new Event('input', { bubbles: true }));
                    item.dispatchEvent(new Event('change', { bubbles: true }));
                }
            };

            const resolveFieldEl = (key) => {
                if (!key) {
                    return null;
                }

                const byId = document.getElementById(key);

                if (byId) {
                    return byId;
                }

                const escaped = (window.CSS && CSS.escape) ? CSS.escape(key) : key;
                return form?.querySelector(`input[type="radio"][id^="${escaped}_"]`)
                    || null;
            };

            const checkValid = (item) => {
                if (!item) {
                    return false;
                }

                if (item.closest('.input-group')) {
                    item.closest('.input-group').classList.remove('has_error');
                    item.closest('.input-group').classList.remove('highlight');

                    // Reset custom validation
                    item.setCustomValidity('');
                } else {
                    item.classList.remove('has_error');
                    item.classList.remove('wrong_number_error');
                    item.classList.remove('highlight');
                }

                normalizeUrlValue(item);

                //RB first/last names validation for openIdFields
                //regexes provided by the RB team
                try {
                    if (!item?.disabled && item?.value?.length) {
                        const fields = this.formBuilder[this.currentStep]['fields'];
                        const found = fields.find(obj => obj.key === item.id);
                        if (found && found.hasOwnProperty('openIdField') && found.openIdField && RBOpenIdRegex.hasOwnProperty(found.openIdField)) {
                            const value = item.value;
                            const regexPattern = RBOpenIdRegex[found.openIdField];
                            const regex = new RegExp(regexPattern);
                            if (!regex.test(value)) {
                                item.setCustomValidity(this.$t('validation.rb_name_validation'));
                            }
                        }
                    }
                } catch (e) {
                    item.setCustomValidity(""); // clear the RB validation error, just in case
                    console.error(e)
                }

                // Validate tel input (advanced type)
                if (item.dataset.hasOwnProperty('hfValidation') && item.dataset.hfValidation === 'tel' && formGlobal.hasOwnProperty(item.id)) {
                    if (formGlobal.hasOwnProperty(item.id) && formGlobal[item.id]
                        && (
                            (formGlobal[item.id].hasOwnProperty('phoneNumber') && formGlobal[item.id]['phoneNumber'] !== undefined)
                            || (formGlobal[item.id].hasOwnProperty('required') && formGlobal[item.id]['required'])
                        )
                        && (!formGlobal[item.id].hasOwnProperty('isValid') || !formGlobal[item.id].isValid)
                    ) {
                        hasErrors = true;

                        if(item.closest('.input-group')) {
                            item.closest('.input-group').classList.add('has_error');
                        } else {
                            item.classList.add('has_error');
                            if(formGlobal[item.id].hasOwnProperty('phoneNumber') && formGlobal[item.id]['phoneNumber'] !== undefined) {
                                item.classList.add('wrong_number_error');
                            }
                        }

                    }
                } else if (item.dataset.hasOwnProperty('hfSliderRequired') && item.dataset.touched !== 'true') {
                    hasErrors = true;
                    const errorMessage = this.$t('form_builder.hosted_form.error.required');

                    item.setCustomValidity(errorMessage);

                    if (item.closest('.input-group')) {
                        item.closest('.input-group').classList.add('has_error');
                        const caution = _.first(item.closest('.input-group').getElementsByClassName('error_text_caution'));
                        if (caution) caution.innerHTML = errorMessage;
                    } else {
                        item.classList.add('has_error');
                    }
                } else if (!item.checkValidity() || isRequiredAndEmpty(item, form)) {
                    hasErrors = true;
                    let errorMessage = item.validationMessage;
                    const valueMissing = item.validity?.valueMissing ?? isRequiredAndEmpty(item, form);

                    if (valueMissing) {
                        errorMessage = this.$t('form_builder.hosted_form.error.required');
                    } else if (item.validity.typeMismatch && item.type === 'email') {
                        errorMessage = this.$t('validation.email', {attribute: this.$t('validation.default_attribute')});
                    } else if (item.validity.tooShort) {
                        errorMessage = this.$t('validation.min.string', {attribute: this.$t('validation.default_attribute'), min: item.minLength});
                    } else if (item.validity.tooLong) {
                        errorMessage = this.$t('validation.max.string', {attribute: this.$t('validation.default_attribute'), min: item.maxLength});
                    } else if (item.validity.rangeUnderflow) {
                        errorMessage = this.$t('validation.min.numeric', { attribute: this.$t('validation.default_attribute'), min: item.min });
                    } else if (item.validity.rangeOverflow) {
                        errorMessage = this.$t('validation.max.numeric', { attribute: this.$t('validation.default_attribute'), max: item.max });
                    } else if (item.validity.typeMismatch && item.type === 'url') {
                        errorMessage = this.$t('validation.url', { attribute: this.$t('validation.default_attribute') });
                    }

                    item.setCustomValidity(errorMessage);

                    if (item.closest('.input-group')) {
                        item.closest('.input-group').classList.add('has_error');
                        _.first(item.closest('.input-group').getElementsByClassName('error_text_caution')).innerHTML = item.validationMessage;
                    } else {
                        item.classList.add('has_error');
                    }
                }
                if (item.type === 'file') {
                    let form = this.$refs.hostedForm,
                        allItems = Array.from(form.getElementsByClassName('input-file')),
                        totalFiles = allItems.reduce((total, input) => total + input.files.length, 0),
                        errorMessage = '',
                        currentStep = form.querySelector('.step-' + this.currentStep);
                    if (totalFiles > this.fileLimit) {
                        errorMessage = this.$t('attachments.max_files_validation', { count: this.fileLimit });
                        hasErrors = true;
                    }
                    allItems.forEach(input => {
                        let inputGroup = input.closest('.input-group');
                        let errorTextElement = inputGroup ? inputGroup.querySelector('.error_text_caution') : null;
                        if (totalFiles > this.fileLimit) {
                            input.setCustomValidity(errorMessage);
                            if (inputGroup) {
                                inputGroup.classList.add('has_error');
                                if (errorTextElement) errorTextElement.innerHTML = errorMessage;
                            }
                        } else if ((input.validity?.valueMissing || (input.required && !input.files?.length)) && currentStep.contains(input)) {
                            errorMessage = this.$t('form_builder.hosted_form.error.required');
                            input.setCustomValidity(errorMessage);
                            hasErrors = true;
                            if (inputGroup) inputGroup.classList.add('has_error');
                            if (errorTextElement) errorTextElement.innerHTML = errorMessage;
                        } else if (!currentStep.contains(input)) {
                            input.setCustomValidity('');
                            if (inputGroup) inputGroup.classList.remove('has_error');
                        } else if ((!input.required || input.files?.length) && currentStep.contains(input)) {
                            input.setCustomValidity('');
                            if (inputGroup) inputGroup.classList.remove('has_error');
                        }
                    });
                }
            }

            if (form && el) {
                let domItem = resolveFieldEl(el);

                if (!domItem) {
                    this.formBuilder[step]['fields'].forEach((item) => {
                        // Validate newsletter required fields
                        if (item.type === 'newsletter' && item.isRequired && item.key === el) {
                            const domItem = document.getElementById(item.key);
                            hasErrors = this.validateNewsLetter(item) || hasErrors;

                            checkValid(domItem);
                        }
                        // Validate signature required fields
                        if (item.type === 'signature' && item.isRequired && item.key === el) {
                            hasErrors = this.validateSignature(item) || hasErrors;
                        }
                        // Validate booking widget fields
                        if (item.type === 'booking_widget' && item.key === el) {
                            hasErrors = this.validateBookingWidget(item) || hasErrors;
                        }
                    });
                }

                checkValid(domItem)
            } else if (!el && form) {
                this.formBuilder[step]['fields'].forEach((item) => {
                    const domItem = resolveFieldEl(item.key);

                    // Validate newsletter required fields
                    if (item.type === 'newsletter' && item.isRequired) {
                        hasErrors = this.validateNewsLetter(item) || hasErrors;
                    }

                    // Validate signature required fields
                    if (item.type === 'signature' && item.isRequired) {
                        hasErrors = this.validateSignature(item) || hasErrors;
                    }

                    // Validate booking widget fields
                    if (item.type === 'booking_widget') {
                        hasErrors = this.validateBookingWidget(item) || hasErrors;
                    }

                    checkValid(domItem);
                });
            }

            this.checkStepErrors();
            return hasErrors;
        },
        validateNewsLetter(item) {
            let hasErrors = false;
            const domItem = document.getElementById(item.key);
            const selected = this.form[item.key] || [];
            const selectedLabels = selected.map(val => val?.toString?.().trim()).filter(Boolean);
            const groupWrapper = domItem?.closest('.input-group') || document.querySelector(`[data-newsletter-group="${item.key}"]`);
            const errorText = groupWrapper?.querySelector('.error_text_caution');

            if (!selectedLabels.length) {
                hasErrors = true;
                if (groupWrapper) {
                    groupWrapper.classList.add('has_error');
                    if (errorText) {
                        errorText.innerHTML = this.$t('form_builder.hosted_form.error.required');
                    }
                }
            } else {
                if (groupWrapper) {
                    groupWrapper.classList.remove('has_error');
                    if (errorText) {
                        errorText.innerHTML = '';
                    }
                }
            }

            return hasErrors;
        },
        updateSignatureValue(fieldKey, eventData) {
            let value, consentText, timestamp;

            if (eventData && typeof eventData === 'object' && !Array.isArray(eventData)) {
                value = eventData.value || '';
                consentText = eventData.consent;
                timestamp = eventData.timestamp;
            } else {
                value = eventData || '';
                const consentInput = document.querySelector(`input[name="${fieldKey}_consent"]`);
                const timestampInput = document.querySelector(`input[name="${fieldKey}_timestamp"]`);

                if (consentInput) {
                    consentText = consentInput.value;
                }

                if (timestampInput) {
                    timestamp = timestampInput.value;
                }
            }

            this.$set(this.form, fieldKey, value);

            if (consentText !== undefined) {
                this.$set(this.form, fieldKey + '_consent', consentText);
            }

            if (timestamp !== undefined) {
                this.$set(this.form, fieldKey + '_timestamp', timestamp);
            }

            this.validateFormStep(this.currentStep, fieldKey);
        },
        validateSignature(item) {
            let hasErrors = false;
            const signatureValue = this.form[item.key] || '';
            const groupWrapper = document.querySelector(`[data-signature-field="${item.key}"]`) ||
                                document.querySelector(`input[name="${item.key}"]`)?.closest('.input-group');
            const errorText = groupWrapper?.querySelector('.error_text_caution');

            if (!signatureValue || signatureValue.trim() === '') {
                hasErrors = true;
                if (groupWrapper) {
                    groupWrapper.classList.add('has_error');
                    if (errorText) {
                        errorText.innerHTML = this.$t('form_builder.hosted_form.error.required');
                    }
                }
            } else {
                if (groupWrapper) {
                    groupWrapper.classList.remove('has_error');
                    if (errorText) {
                        errorText.innerHTML = '';
                    }
                }
            }

            return hasErrors;
        },
        validateBookingWidget(item) {
            // Get selected options from form data
            let selectedOptions = this.form[item.key];

            // Fallback: try to get from DOM if form data is not available
            if (!selectedOptions || !Array.isArray(selectedOptions)) {
                const groupWrapper = document.querySelector(`[data-field-key="${item.key}"]`)?.closest('.input-group.booking-widget');
                if (groupWrapper) {
                    const checkedInputs = groupWrapper.querySelectorAll('input[type="checkbox"]:checked');
                    selectedOptions = Array.from(checkedInputs).map(input => {
                        const label = input.closest('label')?.querySelector('span:not(.form-check-input)')?.textContent?.trim();
                        return label || input.id.replace(item.key + '_', '').replace(/_/g, ' ');
                    }).filter(Boolean);
                } else {
                    selectedOptions = [];
                }
            }

            const selectedCount = Array.isArray(selectedOptions) ? selectedOptions.filter(opt => opt && opt.toString().trim()).length : 0;

            const groupWrapper = document.querySelector(`[data-field-key="${item.key}"]`)?.closest('.input-group.booking-widget') ||
                                 this.$refs.hostedForm?.querySelector('.step-' + this.currentStep)?.querySelector('.input-group.booking-widget');

            if (!groupWrapper) {
                // Fallback validation if wrapper not found
                if (item.isRequired && selectedCount === 0) {
                    return true;
                }
                if (selectedCount > 0) {
                    const minSelection = parseInt(item.minSelection);
                    const maxSelection = parseInt(item.maxSelection);
                    if (minSelection && selectedCount < minSelection) {
                        return true;
                    }
                    if (maxSelection && selectedCount > maxSelection) {
                        return true;
                    }
                }
                return false;
            }

            const errorText = groupWrapper.querySelector('.error_text_caution');

            // Helper to parse selection value
            const parseSelectionValue = (value) => {
                if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
                    return null;
                }
                const parsed = parseInt(value, 10);
                return isNaN(parsed) ? null : parsed;
            };

            // Get minSelection from item (try both bookingMinSelection and minSelection)
            const rawMinSelection = item.bookingMinSelection ?? item.minSelection ?? null;
            const rawMaxSelection = item.bookingMaxSelection ?? item.maxSelection ?? null;

            // If field is required, minimum is 1 (or minSelection if set)
            let minSelection;
            if (item.isRequired) {
                minSelection = parseSelectionValue(rawMinSelection) ?? 1;
            } else {
                // If field is not required:
                // - If nothing selected, skip validation (allow empty)
                if (selectedCount === 0) {
                    groupWrapper.classList.remove('has_error');
                    if (errorText) {
                        errorText.style.display = 'none';
                    }
                    return false;
                }
                // - If something selected, apply min/max rules
                minSelection = parseSelectionValue(rawMinSelection);

                // If minSelection is null but user selected something, no validation needed
                if (minSelection === null) {
                    groupWrapper.classList.remove('has_error');
                    if (errorText) {
                        errorText.style.display = 'none';
                    }
                    return false;
                }
            }

            const maxSelection = parseSelectionValue(rawMaxSelection);

            // Validate max selection
            if (maxSelection && selectedCount > maxSelection) {
                // Only show error after form submission attempt
                if (this.formSubmitted || groupWrapper.classList.contains('has_error')) {
                    groupWrapper.classList.add('has_error');
                    if (errorText) {
                        errorText.innerHTML = this.$t('validation.max.array', {attribute: item.label, max: maxSelection});
                        errorText.style.display = 'block';
                    }
                } else {
                    // Don't show error before form submission
                    groupWrapper.classList.remove('has_error');
                    if (errorText) {
                        errorText.style.display = 'none';
                    }
                }
                return true;
            }

            // Validate min selection
            if (minSelection !== null && selectedCount < minSelection) {
                // Only show error after form submission attempt
                if (this.formSubmitted || groupWrapper.classList.contains('has_error')) {
                    groupWrapper.classList.add('has_error');
                    if (errorText) {
                        const errorMessage = minSelection === 1
                            ? this.$t('validation.required', {attribute: item.label})
                            : this.$t('validation.min.array', {attribute: item.label, min: minSelection});
                        errorText.innerHTML = errorMessage;
                        errorText.style.display = 'block';
                    }
                } else {
                    // Don't show error before form submission
                    groupWrapper.classList.remove('has_error');
                    if (errorText) {
                        errorText.style.display = 'none';
                    }
                }
                return true;
            }

            groupWrapper.classList.remove('has_error');
            if (errorText) {
                errorText.style.display = 'none';
            }
            return false;
        },
        checkStepErrors(reset = false) {
            let form = this.$refs.hostedForm;
            if(!this.showWelcome && !this.showThankYou) {
                this.errorsTotal = form.querySelectorAll('.step-' + this.currentStep + ' .has_error').length
            }

            if(reset && this.errorsTotal === 0) {
                this.showErrorsHelper = false
            }

            if(this.errorsTotal > 0) {
                this.showErrorsHelper = true
            }
        },
        saveFormState() {
            if (this.mode === 'live') {
                let data = {};
                for (const property in this.form) {
                    if (this.form[property]) {
                        data[property] = this.form[property];
                    }
                }

                if (Object.keys(data).length) {
                    localStorage.setItem('hf_form_hosted', JSON.stringify({
                        'id': this.formId,
                        'step': this.currentStep,
                        'data': data
                    }));
                    //add newsletterData
                }
            }
        },
        fetchFormState() {
            if (this.mode === 'live') {
                try {
                    if (localStorage.getItem('hf_form_hosted')) {
                        let localStorageData = JSON.parse(localStorage.getItem('hf_form_hosted'));

                        //check if saved data belongs to the current form
                        if (!localStorageData.hasOwnProperty('id') || localStorageData.id !== this.formId) {
                            this.resetFormState();
                            return false;
                        }

                        //set current step
                        try {
                            if (localStorageData.hasOwnProperty('step')) {
                                let formStep = parseInt(localStorageData.step);
                                if (formStep <= this.formBuilder.length) {
                                    this.currentStep = formStep;
                                }
                            }
                        } catch (e) {
                            console.error('fetchFormState parse step failed');
                        }

                        //fill the form with data from localstorage
                        try {
                            if (localStorageData.hasOwnProperty('data')) {
                                let formData = localStorageData.data;
                                //check if the form has the same fields
                                for (const property in formData) {
                                    if (this.formBuilderFieldsTypes.hasOwnProperty(property)) {
                                        this.form[property] = formData[property];
                                    }
                                }
                            }
                        } catch (e) {
                            console.error('fetchFormState parse data failed');
                        }

                        return false;
                    }
                } catch (e) {
                    console.error('hf_form_hosted fetchFormState failed');
                }
            }
        },
        resetFormState() {
            localStorage.removeItem('hf_form_hosted');
        },
        showErrors() {
            let form = this.$refs.hostedForm,
                errorsFirst = _.first(form.getElementsByClassName('has_error')),
                topPosition = errorsFirst.getBoundingClientRect().top + window.scrollY - 120;
            errorsFirst.classList.add('highlight');
            window.scrollTo({
                top: topPosition,
                behavior: "smooth",
            });
        }
    }
});
